import user from './user';
import moment from 'moment-timezone';

export default new class dates {
  constructor() {
    this.locale = getLocale();
    this.timezone = getTimezone();
    const {dateFormat, timeFormat, dateOnlyFormat, timeOnlyFormat} = getDateAndTimeFormats();
    this.dateFormat = dateFormat;
    this.timeFormat = timeFormat;
    this.dateOnlyFormat = dateOnlyFormat;
    this.timeOnlyFormat = timeOnlyFormat;

    this.isIE = !!document.documentMode;
    this.isEdge = navigator.userAgent.includes("Edge");

    // CONVERT DATE FROM microsoft format TO moment format
    this.convertDateFormat = convertDateFormat;


    // VALIDATE date/time value
    this.validateValue = (value, {withoutTimezone}) => {
      if (value instanceof Date) value = value.toISOString();

      if (
        !value
        || typeof value !== 'string' // if not string
        || !value.trim() // if no value
        || value === '0001-01-01T00:00:00.000Z' // if empty date
        || value === '0001-01-01T00:00:00Z' // if empty date
        || new Date(value).getTime() === 62135596800000 // if date is the odd format of year 3989
        || new Date(value).getTime() === 0 // if date is unix epoch time
      ) {
        return '';
      }
      if (withoutTimezone && value.endsWith('Z')) {
        value = value.slice(0, -1); // removes UTC timezone from value
      }

      // if IE or Edge
      if (!this.isIE && !this.isEdge) {
        value = value.toString(); // Necessary for other browsers, but breaks IE and Edge. (taken from angular1)
      }

      return value;
    }
    // !validateValue()


    // FORMAT DATE
    this.formatDate = (value, params) => {

      const withoutTimezone = params?.withoutTimezone || false;
      const includeTime = params?.includeTime || false;
      const dateOnly = params?.dateOnly || false;
      const timeOnly = params?.timeOnly || false;

      // for timeOnly
      if (timeOnly) {
        return this.formatTime(value, {withoutTimezone, timeOnly});
      }

      // validate value
      value = this.validateValue(value, {withoutTimezone});
      if (!value) return '';

      let formattedDate;

      if (dateOnly) { // for dateOnly

        // removes time from value
        const timeIdx = value.indexOf('T');
        if (timeIdx >= 0) {
          value = value.slice(0, timeIdx);
        }

        const m = moment(value).locale(this.locale);
        formattedDate = m.format(this.dateOnlyFormat);

      } else { // if not dateOnly

        const m = moment(value).locale(this.locale);
        if (!withoutTimezone) m.tz(this.timezone);
        formattedDate = m.format(this.dateFormat);

      }

      // if invalid date
      if (formattedDate.trim() === 'Invalid date') return value;

      // include time
      if (includeTime) {
        const formattedTime = this.formatTime(value, {withoutTimezone});
        formattedDate += ` ${formattedTime}`;
      }

      return formattedDate;
    }
    // !FORMAT DATE


    // FORMAT TIME
    this.formatTime = (value, params) => {
      let withoutTimezone = params?.withoutTimezone || false;
      const timeOnly = params?.timeOnly || false;

      value = this.validateValue(value, {withoutTimezone});
      if (!value) return '';

      const m = moment(value).locale(this.locale);
      if (!withoutTimezone) m.tz(this.timezone);
      const formattedTime = timeOnly ? m.format(this.timeOnlyFormat) : m.format(this.timeFormat);

      // if invalid time
      if (formattedTime === 'Invalid date') return value;

      return formattedTime;
    }
    // !FORMAT TIME


    // FORMAT BY HOUR AND MINUTE FUNCTION
    this.formatTimeByHourAndMinute = (hour, minute) => {
      const datetime = moment().hour(hour).minute(minute);
      const dateStr = datetime.format('YYYY-MM-DD HH:mm')
      return this.formatTime(dateStr, {withoutTimezone: true, timeOnly: true,});
    }

    this.localTime = (value, params) => this.formatTime(value, params);
    this.localDateTime = (value, params) => this.formatDate(value, {includeTime: true, ...params});
    this.localDate = (value, params) => {
      if (typeof params === 'boolean') { // take care of old way to format date. "params" is "includeTime" in this case (true or false)
        return this.formatDate(value, {includeTime: params});
      }
      return this.formatDate(value, params); // new way
    }

    //if today, show time, else show date
    this.formatTimeIfTodayElseDate = (value, params) => {
      const withoutTimezone = params?.withoutTimezone || false;
      const dateOnly = params?.dateOnly || false;
      const timeOnly = params?.timeOnly || false;

      value = this.validateValue(value, {withoutTimezone});
      if (!value) return '';

      const m = moment(value).locale(this.locale);
      if (!withoutTimezone) m.tz(this.timezone);

      const today = moment().locale(this.locale).tz(this.timezone).format('YYYY-MM-DD');
      const date = m.format('YYYY-MM-DD');

      if (today === date) {
        return this.formatTime(value, {withoutTimezone, timeOnly});
      } else {
        return this.formatDate(value, {withoutTimezone, dateOnly});
      }

    }


  } // !constructor
}


function getLocale() {
  let locale;
  try {
    locale = Intl.DateTimeFormat().resolvedOptions().locale;
  } catch (err) {
    console.warn(err);
    try {
      locale = window.navigator.language || navigator.language
    } catch (err) {
      console.warn(err);
      locale = user?.currentUser?.Locale || 'en-US';
    }
  }
  return locale;
}

function getTimezone() {
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    console.warn(err);
    timezone = user?.currentUser?.TimeZone || 'UTC';
  }
  return timezone;
}

function getDateAndTimeFormats() {
  const globalFormat = convertDateFormat(localStorage.getItem('GlobalDateFormat'));
  let dateOnlyFormat = convertDateFormat(localStorage.getItem('GlobalDateOnlyFormat')) || 'L';
  let timeOnlyFormat = convertDateFormat(localStorage.getItem('GlobalTimeOnlyFormat')) || 'LT';
  let dateFormat, timeFormat;

  if (globalFormat) {
    // break date/time
    let hourIdx = globalFormat.toLowerCase().indexOf('h');
    if (hourIdx < 0) hourIdx = globalFormat.toLowerCase().indexOf('k');
    if (hourIdx >= 0) { // if globalFormat includes time format
      dateFormat = globalFormat.slice(0, hourIdx).trim(); // date
      timeFormat = globalFormat.slice(hourIdx, globalFormat.length).trim(); // time
    } else { // if globalFormat doesn't include time format
      dateFormat = globalFormat;
      timeFormat = 'LT';
    }
  }

  if (!dateFormat) dateFormat = 'L';
  if (!timeFormat) timeFormat = 'LT';

  return {dateFormat, timeFormat, dateOnlyFormat, timeOnlyFormat};
}
// !getDateAndTimeFormats()

function convertDateFormat (dateFormat) { // converts datetime from "microsoft" format to "moment" format
  if (!dateFormat) return '';

  // break into array
  const piecesArr = [];
  let strPiece = '';
  dateFormat.split('')
    .forEach(char => {
      if (char.match(/[A-Z]/ig)) {
        strPiece += char;
      } else {
        if (strPiece) piecesArr.push(strPiece);
        strPiece = '';
        if (char) piecesArr.push(char);
      }
    });
  if (strPiece) piecesArr.push(strPiece);

  // convert
  const formattedArr = [];
  piecesArr.forEach(p => {
    if (p === 'yy') p = 'YY';
    else if (p === 'yyyy') p = 'YYYY';
    else if (p === 'd') p = 'D';
    else if (p === 'dd') p = 'DD';
    else if (p === 'tt') p = 'A';
    formattedArr.push(p);
  });

  // join and return
  return formattedArr.join('').trim();
}
// !convertDateFormat()